// Global Instructions Rule Applied!
//
// Highlight rewording (owner 2026-11-05) on Configuration → Follower Agents,
// beside Company short descriptions: the agent rewords the stored highlight
// texts that break the house rules the finalise now applies — a text cut off
// with "…", an investment introduction restating its heading (the
// commitment clause; the date and type on a pinned one) — and a person
// reviews the result here, several at once, instead of opening every fund
// and investor set. Admins only (the routes refuse anyone else).
//
// Preview reads the current sets and counts the offending items (no model
// call); Run rewording queues one run per set (one model call each) through
// the agents' queue, and the list fills as the runs land. The tiles say how
// the review stands; the tabs list this run's plan, the rewordings waiting
// for a sign-off and those signed off (the bulk review table: sign off, edit,
// regenerate, several at once). Chrome: the Queue section's tiles, a
// FormSection, antd Tables under a height cap.
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Table, Tabs, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ReloadOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  fetchRewordedHighlightItems,
  generateHighlightsRewording
} from '../../period_highlights/services/periodHighlightsService.ts'
import type {
  HighlightRewordingManifest,
  HighlightRewordingTarget,
  RewordedHighlightItem,
  RewordedHighlightItems
} from '../../../state_manager/data_models/period_highlights_model.ts'
import { HighlightRewordingReviewTable } from './HighlightRewordingReviewTable.tsx'

const { Text } = Typography

const LIST_SCROLL = { y: 320 }
const SCOPE_LABEL: Record<string, string> = { fund: 'Fund', fund_group: 'Consolidated fund', lp_account: 'LP' }
const EMPTY_CAPS: RewordedHighlightItems['caps'] = { headline: 120, body: 400, short_text: 220 }

const token = () => getAccessToken(store.getState().auth)
const errorText = (error: unknown, fallback: string) => (error instanceof Error ? error.message : fallback)

export function HighlightRewordingSection() {
  const { openFormInTab } = useTabManagerContext()
  const [list, setList] = useState<RewordedHighlightItems | null>(null)
  const [loading, setLoading] = useState(false)
  const [manifest, setManifest] = useState<HighlightRewordingManifest | null>(null)
  const [busy, setBusy] = useState<'preview' | 'run' | null>(null)
  const [activeTab, setActiveTab] = useState('unreviewed')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setList(await fetchRewordedHighlightItems(token()))
    } catch (error) {
      showErrorToast(errorText(error, 'The reworded highlights could not be loaded.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const unreviewedRows = useMemo(() => (list?.items ?? []).filter((row) => row.prog__status === 'unreviewed'), [list])
  const signedOffRows = useMemo(() => (list?.items ?? []).filter((row) => row.prog__status === 'signed_off'), [list])

  const handlePreview = useCallback(async () => {
    setBusy('preview')
    try {
      setManifest(await generateHighlightsRewording({ dry_run: true }, token()))
      setActiveTab('run')
    } catch (error) {
      showErrorToast(errorText(error, 'The rewording could not be previewed.'))
    } finally {
      setBusy(null)
    }
  }, [])

  const handleRun = useCallback(async () => {
    setBusy('run')
    try {
      const result = await generateHighlightsRewording({ dry_run: false }, token())
      setManifest(result)
      setActiveTab('run')
      if (result.enqueued > 0) {
        toast.success(
          `${result.enqueued} rewording run${result.enqueued === 1 ? '' : 's'} queued — the reworded highlights appear under To sign off as the runs land`
        )
      } else {
        toast.info('Nothing to reword: every selected highlight obeys the rules.')
      }
      if (result.enqueue_errors) showErrorToast(`${result.enqueue_errors} set${result.enqueue_errors === 1 ? '' : 's'} could not be queued.`)
    } catch (error) {
      showErrorToast(errorText(error, 'The rewording could not be queued.'))
    } finally {
      setBusy(null)
    }
  }, [])

  const openSet = useCallback(
    (row: RewordedHighlightItem) =>
      openFormInTab('period-highlights', row.highlight_sets__id, `${row.prog__scope_name ?? 'Highlights'} · ${row.prog__period_label ?? ''}`),
    [openFormInTab]
  )

  const targetColumns: ColumnsType<HighlightRewordingTarget> = [
    {
      title: 'Set',
      key: 'set',
      width: 260,
      render: (_: unknown, t) => (
        <button
          type='button'
          className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
          onClick={() => openFormInTab('period-highlights', t.set_id, `${t.name ?? 'Highlights'} · ${t.period_label ?? t.period_end}`)}
        >
          {t.name || SCOPE_LABEL[t.scope_type] || t.scope_type} · {t.period_label || t.period_end}
        </button>
      )
    },
    { title: 'Scope', key: 'scope', width: 130, render: (_: unknown, t) => SCOPE_LABEL[t.scope_type] || t.scope_type },
    { title: 'Highlights', key: 'items', width: 90, dataIndex: 'items', align: 'right' as const },
    { title: 'Why', key: 'reasons', render: (_: unknown, t) => t.reasons.join('; ') },
    ...(manifest && !manifest.dry_run
      ? [{ title: 'Queued', key: 'status', width: 100, render: (_: unknown, t: HighlightRewordingTarget) => t.status ?? '—' }]
      : [])
  ]

  const tile = (label: string, value: React.ReactNode, hint?: string) => (
    <Tooltip title={hint}>
      <div>
        <span className='block text-[10.5px] tracking-wider uppercase text-gray-400'>{label}</span>
        <span className='text-base font-semibold tabular-nums'>{value}</span>
      </div>
    </Tooltip>
  )

  const headerActions = (
    <div className='flex items-center gap-2'>
      <Button size='small' icon={<ReloadOutlined />} disabled={loading} onClick={() => void load()} />
      <Button size='small' loading={busy === 'preview'} disabled={busy != null} onClick={() => void handlePreview()}>
        Preview
      </Button>
      <Tooltip title={manifest?.dry_run ? undefined : 'Preview first: Run queues the sets the preview lists.'}>
        <Button
          size='small'
          type='primary'
          loading={busy === 'run'}
          disabled={busy != null || !manifest?.dry_run || manifest.sets_to_update === 0}
          onClick={() => void handleRun()}
        >
          Run rewording
        </Button>
      </Tooltip>
    </div>
  )

  const runRows = manifest ? (manifest.dry_run ? manifest.targets : manifest.results) : []

  return (
    <FormSection
      title='Highlight rewording'
      titleColor='purple'
      loading={loading && !list}
      className='shadow-sm border border-gray-200'
      headerActions={headerActions}
    >
      <p className='text-gray-500 text-xs mt-0 mb-2'>
        The period highlights the fund, consolidated-fund and investor trackers wrote before the house rules changed: a
        text cut off with “…”, or a new-investment introduction that restates its heading (the commitment, the date, the
        type). Preview counts them across every current set without a model call; Run rewording asks the agent for new
        wording, one run per set, written in place with the previous text kept. A person’s hand-edited highlight is never
        touched. Review the result below, several at once: a set whose only change since its sign-off is the rewording is
        signed off again with its lines; a set nobody had signed off stays “not reviewed” until it is read on its own form.
      </p>
      <div className='flex gap-6 flex-wrap items-center bg-white border border-gray-100 rounded-lg px-4 py-2.5 mb-3'>
        {tile('Sets to reword', manifest ? manifest.sets_to_update : '—', 'Current sets with at least one highlight that breaks a rule (Preview).')}
        {tile('Highlights to reword', manifest ? manifest.items_to_reword : '—', 'Selected highlights that break a rule, across those sets (Preview).')}
        {tile('To sign off', unreviewedRows.length, 'Reworded by the agent and not yet signed off.')}
        {tile('Signed off', signedOffRows.length, 'Reworded and signed off here.')}
      </div>
      <Tabs
        size='small'
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          ...(manifest
            ? [
                {
                  key: 'run',
                  label: manifest.dry_run
                    ? `Preview (${manifest.sets_to_update} set${manifest.sets_to_update === 1 ? '' : 's'})`
                    : `This run (${manifest.enqueued} queued)`,
                  children: (
                    <div className='space-y-2'>
                      <Text type='secondary' className='text-xs block'>
                        {manifest.sets_seen} current set{manifest.sets_seen === 1 ? '' : 's'} read, {manifest.items_seen} selected
                        highlight{manifest.items_seen === 1 ? '' : 's'}
                        {manifest.items_edited ? ` (${manifest.items_edited} edited by a person, left alone)` : ''}.
                        {manifest.reasons.length
                          ? ' ' + manifest.reasons.map((r) => `${r.items} × ${r.reason}`).join(', ') + '.'
                          : ' Nothing breaks a rule.'}
                        {!manifest.dry_run && manifest.enqueued > 0
                          ? ` ${manifest.enqueued} run${manifest.enqueued === 1 ? '' : 's'} queued, ${manifest.stagger_minutes} minute${manifest.stagger_minutes === 1 ? '' : 's'} apart.`
                          : ''}
                      </Text>
                      {runRows.length === 0 ? (
                        <Text type='secondary'>No set needs rewording.</Text>
                      ) : (
                        <Table<HighlightRewordingTarget>
                          size='small'
                          pagination={false}
                          rowKey='set_id'
                          columns={targetColumns}
                          dataSource={runRows}
                          scroll={LIST_SCROLL}
                          tableLayout='auto'
                        />
                      )}
                    </div>
                  )
                }
              ]
            : []),
          {
            key: 'unreviewed',
            label: `To sign off (${unreviewedRows.length})`,
            children: (
              <HighlightRewordingReviewTable
                rows={unreviewedRows}
                caps={list?.caps ?? EMPTY_CAPS}
                openSet={openSet}
                onChanged={load}
                empty='Every reworded highlight is signed off.'
              />
            )
          },
          {
            key: 'signed_off',
            label: `Signed off (${signedOffRows.length})`,
            children: (
              <HighlightRewordingReviewTable
                rows={signedOffRows}
                caps={list?.caps ?? EMPTY_CAPS}
                openSet={openSet}
                onChanged={load}
                empty='No reworded highlight is signed off yet.'
              />
            )
          }
        ]}
      />
    </FormSection>
  )
}

export default HighlightRewordingSection
